import { useState, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ConfirmDialog } from "@/components/ui-system";
import {
  apiErrorMessage, useTemplateRecipients, RecipientsCard, EmailPreviewHeader,
} from "@/components/EmailTemplateRecipients";
import { toast } from "sonner";
import { Plus, Trash2, Send, Loader2, Layers, Settings } from "lucide-react";

/**
 * Demurrage Charges Email Notification — a manual letter asking the carrier to
 * explain the demurrage charged on a list of LRNs.
 *
 * Recipients are the saved notification_configs doc for DEMURRAGE_ACTION_TYPE
 * (shared Recipients card); Trigger Now posts ONLY the LRNs to
 * POST /notification-config/admin/demurrage-charges/send, which sends to the saved
 * To/CC/BCC (backend/services/demurrage_charges_service.py).
 *
 * The preview wording mirrors render() in that service — change both together.
 */
export const DEMURRAGE_ACTION_TYPE = "demurrage_charges";

// Mirrors backend/models/demurrage_charges.py — the backend re-validates.
const MAX_LRNS = 100;
const LRN_RE = /^[A-Za-z0-9-]{1,40}$/;

const SUBJECT = "Demurrage Charges – LRNs";

let seq = 0;
const emptySegment = () => ({ key: `dmr-${++seq}`, value: "" });

/** LRNs typed into one segment: separated by commas, spaces or new lines. */
const tokens = (value) => value.split(/[\s,;]+/).map((t) => t.trim()).filter(Boolean);

/**
 * Per-segment errors plus the de-duplicated LRN list the email will carry
 * (first-seen order; the same LRN in two segments is listed once).
 */
function analyse(segments) {
  const errors = {};
  const seen = new Set();
  const lrns = [];
  let duplicates = 0;
  segments.forEach((seg) => {
    const list = tokens(seg.value);
    const bad = list.filter((t) => !LRN_RE.test(t));
    if (!list.length) errors[seg.key] = "Enter at least one LRN, or delete this segment";
    else if (bad.length) errors[seg.key] = `Invalid LRN(s): ${bad.join(", ")} — letters, digits and hyphens only`;
    list.filter((t) => LRN_RE.test(t)).forEach((t) => {
      const k = t.toUpperCase();
      if (seen.has(k)) { duplicates += 1; return; }
      seen.add(k);
      lrns.push(t);
    });
  });
  const overCap = lrns.length > MAX_LRNS;
  return { errors, lrns, duplicates, overCap, count: Object.keys(errors).length + (overCap ? 1 : 0) };
}

function EmailPreview({ sender, to, cc, bcc, lrns }) {
  return (
    <div className="text-sm">
      <EmailPreviewHeader sender={sender} to={to} cc={cc} bcc={bcc}
        subject={SUBJECT} subjectTestId="dmr-preview-subject" />
      <div className="pt-3 space-y-3 text-gray-800" data-testid="dmr-preview-body">
        <p>Hi Delhivery Team,</p>
        <p>Below are some LRNs with demurrage charges applied. Kindly provide clarification at the earliest.</p>
        <div className="rounded-lg bg-slate-100 px-4 py-3">
          <p className="font-medium">LRNs:</p>
          {lrns.length ? (
            <ul className="list-disc pl-5" data-testid="dmr-preview-lrns">
              {lrns.map((lrn) => <li key={lrn} className="break-all">{lrn}</li>)}
            </ul>
          ) : (
            <p className="text-gray-400">—</p>
          )}
        </div>
        <p>
          Please investigate and share the reason for the demurrage charges and whether any action is
          required from our end.
        </p>
        <p>Thanks &amp; Regards,<br />{sender?.from_name || "EdgeOS Support Team"}</p>
      </div>
    </div>
  );
}

export default function DemurrageChargesPanel({ user, configs, loading, onConfigSaved }) {
  const recipients = useTemplateRecipients({
    actionType: DEMURRAGE_ACTION_TYPE, senderPath: "demurrage-charges/sender",
    user, configs, onConfigSaved,
  });
  const { to, cc, bcc, sender, sendBlocker } = recipients;

  const [segments, setSegments] = useState(() => [emptySegment()]);
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const { errors, lrns, duplicates, overCap, count: errorCount } = useMemo(() => analyse(segments), [segments]);

  const setSegmentValue = (key, value) =>
    setSegments((prev) => prev.map((s) => (s.key === key ? { ...s, value } : s)));

  const requestSend = () => {
    setShowAllErrors(true);
    if (sendBlocker) { toast.error(sendBlocker); return; }
    if (errorCount) { toast.error("Fix the highlighted LRN segments before sending"); return; }
    setConfirmOpen(true);
  };

  const send = async () => {
    if (sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/notification-config/admin/demurrage-charges/send`, { lrns });
      if (res.data?.success) {
        toast.success(res.data.message || "Email sent");
        // Cleared so a second click cannot resend the same request by accident.
        setSegments([emptySegment()]);
        setShowAllErrors(false);
      } else {
        toast.error(res.data?.error || "The email could not be sent");
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, "The email could not be sent"));
    } finally {
      setSending(false);
      setConfirmOpen(false);
    }
  };

  if (loading && !configs.length) {
    return (
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6" aria-busy="true">
        {[0, 1].map((i) => <div key={i} className="h-72 rounded-xl bg-slate-100 animate-pulse" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="demurrage-charges-panel">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <Settings className="w-5 h-5 text-teal-600 shrink-0" />
          Demurrage Charges Email Notification Template
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          This template is used to send notifications for demurrage charges applied to LRNs. It alerts
          stakeholders about demurrage charges and requires clarification from the Delhivery team.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        <div className="space-y-6 min-w-0">
          <RecipientsCard recipients={recipients} idPrefix="dmr"
            description="Send notifications to the users who need to be aware of demurrage charges." />

          {/* Segments — per-email LRNs, never saved */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Layers className="w-5 h-5 text-gray-500" />Segments
                <Badge variant="secondary" className="text-xs">{segments.length}</Badge>
              </CardTitle>
              <p className="text-xs text-gray-500">
                Enter the LRNs with demurrage charges, separated by commas. Use segments to group them;
                every LRN is listed once in the email. Up to {MAX_LRNS} LRNs per email.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {segments.map((seg, i) => {
                const segLrns = tokens(seg.value);
                const err = (showAllErrors || seg.value.trim()) ? errors[seg.key] : null;
                const inputId = `${seg.key}-lrns`;
                return (
                  <div key={seg.key} className="rounded-lg border border-red-200 bg-red-50/40 p-3"
                    data-testid={`dmr-segment-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-gray-800">Segment {i + 1}</p>
                      <Button type="button" variant="ghost" size="icon" className="shrink-0 text-red-500 hover:text-red-700"
                        disabled={segments.length === 1}
                        title={segments.length === 1 ? "At least one segment is required" : "Delete segment"}
                        aria-label={`Delete segment ${i + 1}`}
                        onClick={() => setSegments((prev) => prev.filter((s) => s.key !== seg.key))}
                        data-testid={`dmr-remove-segment-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <label htmlFor={inputId} className="block text-xs text-gray-600 mt-1">LRNs</label>
                    <Textarea id={inputId} rows={2} value={seg.value}
                      placeholder="e.g. 312604074, 313631461, 313466435"
                      aria-invalid={Boolean(err)}
                      className={`mt-1 bg-white ${err ? "border-red-500" : ""}`}
                      onChange={(e) => setSegmentValue(seg.key, e.target.value)}
                      data-testid={`dmr-lrns-input-${i}`} />
                    {err && <p className="text-xs text-red-600 mt-1">{err}</p>}
                    <p className="text-xs text-gray-500 mt-1 text-right">
                      LRNs in this segment: <span className="tabular-nums">{segLrns.length}</span>
                    </p>
                  </div>
                );
              })}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Button type="button" variant="outline" onClick={() => setSegments((prev) => [...prev, emptySegment()])}
                  data-testid="dmr-add-segment">
                  <Plus className="w-4 h-4 mr-1" />Add Segment
                </Button>
                <span className="text-sm text-gray-700" data-testid="dmr-total-lrns">
                  Total LRNs: <span className="font-medium tabular-nums">{lrns.length}</span>
                </span>
              </div>
              {duplicates > 0 && (
                <p className="text-xs text-amber-700">
                  {duplicates} repeated LRN{duplicates === 1 ? "" : "s"} will be listed only once.
                </p>
              )}
              {overCap && (
                <p className="text-xs text-red-600">
                  {lrns.length} LRNs entered — the limit is {MAX_LRNS} per email. Split them across emails.
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Preview — sticky beside the form on wide screens, below it otherwise */}
        <Card className="min-w-0 xl:sticky xl:top-4 xl:max-h-[calc(100dvh-2rem)] xl:overflow-y-auto">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-lg">Email Preview</CardTitle>
              <Button type="button" className="bg-teal-600 hover:bg-teal-700"
                onClick={requestSend} disabled={sending} data-testid="dmr-trigger-now">
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                Trigger Now
              </Button>
            </div>
            {sendBlocker && <p className="text-xs text-amber-700">{sendBlocker}</p>}
            {!sendBlocker && showAllErrors && errorCount > 0 && (
              <p className="text-xs text-red-600">Fix the highlighted LRN segments before sending.</p>
            )}
          </CardHeader>
          <CardContent>
            <EmailPreview sender={sender} to={to} cc={cc} bcc={bcc} lrns={lrns} />
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(o) => { if (!sending) setConfirmOpen(o); }}
        title="Send Demurrage Charges email?"
        description={`"${SUBJECT}" listing ${lrns.length} LRN(s) will be emailed to ${to.length} To, ${cc.length} CC and ${bcc.length} BCC recipient(s). This sends a real email and cannot be undone.`}
        confirmLabel="Send Email"
        loading={sending}
        onConfirm={send}
      />
    </div>
  );
}
